import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────
const PAGE_W = 595.28;
const MARGIN = 20;
const CONTENT_W = PAGE_W - MARGIN * 2;   // 555.28 pt  ← table must exactly fill this
const FONT = "courier";
const LINE_H = 13;

// Column widths — MUST sum exactly to CONTENT_W (555.28)
// Sum is ~514 -> scale up below
const RAW_COLS = [20, 115, 48, 26, 45, 48, 36, 40, 36, 40, 60];
const RAW_TOTAL = RAW_COLS.reduce((a, b) => a + b, 0);            // 514
const SCALE = CONTENT_W / RAW_TOTAL;
const COL_W = RAW_COLS.map((w) => parseFloat((w * SCALE).toFixed(2)));
// Correct rounding drift on last column so sum = CONTENT_W exactly
COL_W[COL_W.length - 1] = parseFloat(
    (CONTENT_W - COL_W.slice(0, -1).reduce((a, b) => a + b, 0)).toFixed(2)
);

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const str = (v) => (v === null || v === undefined ? "" : String(v));
const formatDate = (d) => (d ? dayjs(d).format("DD-MMM-YY") : "");
const f2 = (v) => Number(v ?? 0).toFixed(2);
const sum = (arr, key) => arr.reduce((s, r) => s + Number(r[key] ?? 0), 0);

/**
 * Draw a block of key | value rows inside a pre-drawn rect.
 * @param {jsPDF} doc
 * @param {Array}  rows     [[label, value], ...]
 * @param {number} x        left edge of the column
 * @param {number} startY   y of first text baseline
 * @param {number} labelW   pixel width reserved for label (pipe placed right after)
 * @param {number} colW     total available width of this column (for value truncation)
 */
function drawKVRows(doc, rows, x, startY, labelW, colW) {
    const pipeX = x + labelW + 2;
    const valueX = pipeX + 6;
    const maxValW = colW - labelW - 10;
    let y = startY;
    rows.forEach(([k, v]) => {
        doc.text(str(k), x, y);
        doc.text("|", pipeX, y);
        const safe = doc.splitTextToSize(str(v), maxValW)[0] || "";
        doc.text(safe, valueX, y);
        y += LINE_H;
    });
}

// ─────────────────────────────────────────────
// GROUP FLAT ROWS → INVOICE OBJECTS
// ─────────────────────────────────────────────
function groupInvoices(data) {
    const grouped = {};
    data.forEach((row) => {
        const key = row.invoice_no;
        if (!grouped[key]) grouped[key] = { ...row, items: [] };
        grouped[key].items.push({
            packet_name: row.packet_name,
            sac_no: row.sac_no,
            lt_kg: row.lt_kg,
            qty: row.qty,
            rate: row.rate,
            discount: row.discount,
            sgst_tax: row.sgst_tax,
            sgst_value: row.sgst_value,
            cgst_tax: row.cgst_tax,
            cgst_value: row.cgst_value,
            igst_value: row.igst_value,
            net_amount: row.net_amount,
        });
    });
    return Object.values(grouped);
}

// ─────────────────────────────────────────────
// DRAW HEADER
// ─────────────────────────────────────────────
function drawHeader(doc, invoice, y) {
    const cx = PAGE_W / 2;
    const halfW = CONTENT_W / 2;

    // ── Company name ──────────────────────────────────────────────────────────
    doc.setFont(FONT, "bold");
    doc.setFontSize(13);
    const companyLines = doc.splitTextToSize(
        "Chamarajanagar District Co operative Milk Producers Union (CHAMUL),",
        CONTENT_W
    );
    companyLines.forEach((line) => { doc.text(line, cx, y, { align: "center" }); y += 15; });

    // ── Address / GSTIN ───────────────────────────────────────────────────────
    doc.setFont(FONT, "normal");
    doc.setFontSize(9);
    doc.text(
        "Kuderu Village, Santhemarahalli Hobli, Chamarajanagar Dist, Karnataka -571316, Mob: 08226 – 234848",
        cx, y, { align: "center" }
    );
    y += 12;
    doc.text("GSTIN: 29AACAC2089A1Z1", cx, y, { align: "center" });
    y += 12;

    // ── Phone / Email ─────────────────────────────────────────────────────────
    doc.setFontSize(8.5);
    doc.text(`Ph No: ${str(invoice.phone_no)}`, MARGIN, y);
    doc.text(`e-Mail: ${str(invoice.e_mail)}`, PAGE_W - MARGIN, y, { align: "right" });
    y += 9;



    // ── Invoice label (centered & properly spaced) ─────────────────────
    // ── Top border line ─────────────────────────
    doc.setLineWidth(1);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);

    // Define exact box height for title area
    const titleBoxHeight = 22;   // you can adjust 20–24 if needed

    // Draw bottom line first (so we know box height)
    doc.line(MARGIN, y + titleBoxHeight, PAGE_W - MARGIN, y + titleBoxHeight);

    // Now perfectly center text inside that box
    doc.setFont(FONT, "bold");
    doc.setFontSize(12);

    const invoiceTitle = str(invoice.label) || "BILL OF SUPPLY";
    const centerX = MARGIN + CONTENT_W / 2;
    const centerY = y + titleBoxHeight / 2 + 4;
    // +4 adjusts for Courier baseline alignment

    doc.text(invoiceTitle, centerX, centerY, { align: "center" });

    // Move Y below box
    y += titleBoxHeight + 6;



    doc.setLineWidth(0.4);

    // ── Meta box (two columns) ────────────────────────────────────────────────
    const leftMeta = [
        ["Reverse Charge", "No"],
        ["Invoice No", str(invoice.invoice_no)],
        ["Invoice Date", formatDate(invoice.invoice_date)],
        ["State", str(invoice.state_name)],
        ["State Code", "29"],
    ];
    const rightMeta = [
        ["Route Name", str(invoice.route_name)],
        ["Gp Number", str(invoice.gp_number)],
        ["Vehicle No", ""],
        ["Date of Supply", ""],
        ["Place Of Supply", ""],
    ];

    const metaRows = Math.max(leftMeta.length, rightMeta.length);
    const metaH = metaRows * LINE_H + 10;

    doc.rect(MARGIN, y, CONTENT_W, metaH);
    doc.line(MARGIN + halfW, y, MARGIN + halfW, y + metaH);

    doc.setFont(FONT, "normal");
    doc.setFontSize(8.5);

    // label width 82pt covers "Place Of Supply" (15 chars @ 8.5pt Courier ≈ 82pt)
    drawKVRows(doc, leftMeta, MARGIN + 3, y + 11, 82, halfW - 3);
    drawKVRows(doc, rightMeta, MARGIN + halfW + 3, y + 11, 82, halfW - 3);

    y += metaH + 4;

    // ── IRN row ───────────────────────────────────────────────────────────────
    const irnH = LINE_H + 8;
    doc.rect(MARGIN, y, CONTENT_W, irnH);
    drawKVRows(doc, [["IRN No", str(invoice.irn_no)]], MARGIN + 3, y + 11, 52, CONTENT_W - 6);
    y += irnH + 4;

    // ── Receiver table ────────────────────────────────────────────────────────
    const receiverRows = [
        ["Receiver Name", str(invoice.customer_name)],
        ["Address", str(invoice.cust_address)],
        ["City", str(invoice.taluka_name)],
        ["District", str(invoice.district_name)],
        ["GSTIN", str(invoice.tin) || "—"],
        ["State", str(invoice.state_name)],
        ["State Code", "29"],
    ];

    const recH = receiverRows.length * LINE_H + 10;
    doc.rect(MARGIN, y, CONTENT_W, recH);
    drawKVRows(doc, receiverRows, MARGIN + 3, y + 11, 82, CONTENT_W - 6);
    y += recH + 6;

    return y;
}

// ─────────────────────────────────────────────
// DRAW ITEMS AUTOTABLE  (fills full CONTENT_W)
// ─────────────────────────────────────────────
function drawItemsTable(doc, items, startY, globalOffset, isLastChunk, allItems) {
    const head = [
        [
            { content: "Sl. No", rowSpan: 2 },
            { content: "Description Of Goods", rowSpan: 2 },
            { content: "HSN\nACS", rowSpan: 2 },
            { content: "Qty", rowSpan: 2 },
            { content: "Rate", rowSpan: 2 },
            { content: "Taxable Value", rowSpan: 2 },
            { content: "SGST", colSpan: 2 },
            { content: "CGST", colSpan: 2 },
            { content: "Net Amount", rowSpan: 2 },
        ],
        [
            { content: "Tax" },
            { content: "Value" },
            { content: "Tax" },
            { content: "Value" },
        ]
    ];

    const body = items.map((item, i) => {
        const taxable = Number(item.qty ?? 0) * Number(item.rate ?? 0) - Number(item.discount ?? 0);
        return [
            str(i + 1 + globalOffset),
            str(item.packet_name),
            str(item.sac_no),
            str(item.qty),
            f2(item.rate),
            f2(taxable),
            str(item.sgst_tax ?? 0),
            f2(item.sgst_value),
            str(item.cgst_tax ?? 0),
            f2(item.cgst_value),
            f2(item.net_amount),
        ];
    });

    if (isLastChunk) {
        const totalTaxable = allItems.reduce(
            (s, r) => s + Number(r.qty ?? 0) * Number(r.rate ?? 0) - Number(r.discount ?? 0), 0
        );
        body.push([
            { content: "Total", colSpan: 5, styles: { fontStyle: "bold", halign: "center" } },
            { content: f2(totalTaxable), styles: { fontStyle: "bold", halign: "right" } },
            { content: "" },
            { content: f2(sum(allItems, "sgst_value")), styles: { fontStyle: "bold", halign: "right" } },
            { content: "" },
            { content: f2(sum(allItems, "cgst_value")), styles: { fontStyle: "bold", halign: "right" } },
            { content: f2(sum(allItems, "net_amount")), styles: { fontStyle: "bold", halign: "right" } },
        ]);
    }

   

    autoTable(doc, {
        head,
        body,
        startY,
        // Pin left and right margins so table spans full content width
        margin: { left: MARGIN, right: MARGIN },
        tableWidth: CONTENT_W,                        // ← forces full width
        styles: {
            font: FONT,
            fontSize: 8,
            cellPadding: 2,
            lineColor: [0, 0, 0],
            lineWidth: 0.3,
            textColor: [0, 0, 0],
            overflow: "linebreak",
        },
        headStyles: {
            fillColor: [220, 220, 220],
            textColor: [0, 0, 0],
            fontStyle: "bold",
            halign: "center",
            valign: "middle",
            minCellHeight: 26,
        },
        columnStyles: {
            0: { halign: "center", cellWidth: COL_W[0] },  // Sl No
            1: { halign: "left", cellWidth: COL_W[1] },  // Description
            2: { halign: "center", cellWidth: COL_W[2] },  // HSN
            3: { halign: "center", cellWidth: COL_W[3] },  // Qty
            4: { halign: "right", cellWidth: COL_W[4] },  // Rate
            5: { halign: "right", cellWidth: COL_W[5] },  // Taxable Value
            6: { halign: "center", cellWidth: COL_W[6] },  // SGST Tax%
            7: { halign: "right", cellWidth: COL_W[7] },  // SGST Value
            8: { halign: "center", cellWidth: COL_W[8] },  // CGST Tax%
            9: { halign: "right", cellWidth: COL_W[9] },  // CGST Value
            10: { halign: "right", cellWidth: COL_W[10] },  // Net Amount
        },
        didParseCell(data) {
            if (data.section === "body") data.cell.styles.fillColor = [255, 255, 255];
        },
        theme: "grid",
    });

    return doc.lastAutoTable.finalY;
}

// ─────────────────────────────────────────────
// DRAW FOOTER
// ─────────────────────────────────────────────
function drawFooter(doc, invoice, items, y) {
    const totalTaxable = items.reduce(
        (s, r) => s + Number(r.qty ?? 0) * Number(r.rate ?? 0) - Number(r.discount ?? 0), 0
    );
    const totalCGST = sum(items, "cgst_value");
    const totalSGST = sum(items, "sgst_value");
    const totalIGST = sum(items, "igst_value");
    const taxTotal = totalCGST + totalSGST + totalIGST;
    const netTotal = sum(items, "net_amount");
    const halfW = CONTENT_W / 2;
    const rv = PAGE_W - MARGIN - 4;

    y += 8;

    // ── Amount in words + numeric summary ────────────────────────────────────
    const summaryRows = [
        ["Total Amount Before Tax", f2(totalTaxable), false],
        ["Add : CGST", f2(totalCGST), false],
        ["Add : SGST", f2(totalSGST), false],
        ["Add : IGST", f2(totalIGST), false],
        ["Tax Total", f2(taxTotal), false],
        ["TCS AMT", str(invoice.tcs_amt) || "—", false],
        ["Total Amount After Tax", f2(netTotal), true],
        ["GST Payable on Rev. Charge", "N.A", false],
    ];
    const boxH = summaryRows.length * 11 + 14;

    doc.setLineWidth(0.4);
    doc.rect(MARGIN, y, CONTENT_W, boxH);
    doc.line(MARGIN + halfW, y, MARGIN + halfW, y + boxH);

    // Left: in words
    doc.setFont(FONT, "bold");
    doc.setFontSize(8.5);
    doc.text("Invoice Value (In Words) :", MARGIN + 4, y + 10);
    doc.setFont(FONT, "normal");
    doc.setFontSize(8);
    doc.splitTextToSize(str(invoice.amt_words), halfW - 10).slice(0, 8).forEach((line, li) => {
        doc.text(line, MARGIN + 4, y + 22 + li * 10);
    });

    // Right: summary
    const rx = MARGIN + halfW + 4;
    doc.setFontSize(8);
    summaryRows.forEach(([label, val, bold], si) => {
        doc.setFont(FONT, bold ? "bold" : "normal");
        doc.text(label, rx, y + 10 + si * 11);
        doc.text(val, rv, y + 10 + si * 11, { align: "right" });
    });
    y += boxH + 6;

    // ── Declaration + Signature ───────────────────────────────────────────────
    const declH = 75;
    doc.rect(MARGIN, y, CONTENT_W, declH);
    doc.line(MARGIN + halfW, y, MARGIN + halfW, y + declH);

    // Left
    doc.setFont(FONT, "bold");
    doc.setFontSize(8.5);
    doc.text("DECLARATION:", MARGIN + 4, y + 10);
    doc.text("CHAMUL PRODUCT WARRANTY", MARGIN + 4, y + 20);
    doc.setFont(FONT, "normal");
    doc.setFontSize(7.5);
    doc.splitTextToSize(
        "We hereby certify that its products mentioned in the said invoices are " +
        "warranted to be of the nature and quality which they are purported to be.",
        halfW - 8
    ).slice(0, 5).forEach((line, li) => {
        doc.text(line, MARGIN + 4, y + 31 + li * 9);
    });

    // Right
    doc.setFont(FONT, "normal");
    doc.setFontSize(7.5);
    doc.splitTextToSize(
        "Certified that the Particulars given above are true and correct",
        halfW - 8
    ).forEach((line, li) => {
        doc.text(line, rv, y + 10 + li * 9, { align: "right" });
    });
    doc.setFont(FONT, "bold");
    doc.setFontSize(8);
    doc.text("for Chamrajanagar Co-Op. Milk Union Ltd.,", rv, y + 44, { align: "right" });
    doc.text("Authorised Signatory", rv, y + 65, { align: "right" });

    y += declH + 4;

    // ── E&OE ─────────────────────────────────────────────────────────────────
    doc.setFont(FONT, "normal");
    doc.setFontSize(7);
    doc.text("[E&OE]", PAGE_W - MARGIN, y, { align: "right" });
}

// ─────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────

/**
 * @param {Array}    reportsData
 * @param {Object}   options
 * @param {boolean}  [options.download]   true → save file, false → open new tab
 * @param {string}   [options.filename]
 * @param {Function} [options.onStart]
 * @param {Function} [options.onFinish]
 */
export function generateTaxInvoicePDF(reportsData = [], options = {}) {
    const { download = false, filename, onStart, onFinish } = options;

    if (!reportsData?.length) return;
    onStart?.();

    try {
        const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
        const invoices = groupInvoices(reportsData);
        const ROWS_PER_PAGE = 23;
        let isFirstPage = true;

        invoices.forEach((invoice) => {
            const items = invoice.items || [];
            const totalChunks = Math.max(1, Math.ceil(items.length / ROWS_PER_PAGE));

            for (let ci = 0; ci < totalChunks; ci++) {
                const isFirstChunk = ci === 0;
                const isLastChunk = ci === totalChunks - 1;
                const pageItems = items.slice(ci * ROWS_PER_PAGE, (ci + 1) * ROWS_PER_PAGE);

                if (!isFirstPage) doc.addPage();
                isFirstPage = false;

                let y = MARGIN;

                if (isFirstChunk) y = drawHeader(doc, invoice, y);
                y = drawItemsTable(doc, pageItems, y, ci * ROWS_PER_PAGE, isLastChunk, items);
                // if (isLastChunk) drawFooter(doc, invoice, items, y);
                if (isLastChunk) {
                    // prevent footer cut
                    if (y + 190 > doc.internal.pageSize.height) {
                        doc.addPage();
                        y = MARGIN;
                    }

                    drawFooter(doc, invoice, items, y);
                }
            }
        });

        // const defaultName = `Tax_Invoice_${Date.now()}.pdf`;
        // if (download) {
        //     doc.save(filename || defaultName);
        // } else {
        //     window.open(URL.createObjectURL(doc.output("blob")));
        // }

        const base64PDF = doc.output("datauristring").split(",")[1];

        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: "download_pdf",
                    fileName: filename || `Tax_Invoice_${Date.now()}.pdf`,
                    base64: base64PDF
                })
            );
        }
    } catch (err) {
        console.error("Tax Invoice PDF generation error:", err);
    } finally {
        onFinish?.();
    }
}